import React, { useEffect, useState, useCallback } from "react";
import { Tab, Tabs, AppBar, useTheme, Paper } from "@material-ui/core";
import TabPanel from "../components/TabPanel";
import ServiceHistory from "./ServiceHistory";
import SafetyCheck from "./SafetyCheck";
import Sockette from "sockette";
import useWorkOrders from "../hooks/useWorkOrders";
import Loading from "../components/Loading";
import useLoading from "../hooks/useLoading";
import useStore from "../hooks/useStore";
import useLicensePlates from "../hooks/useLicensePlates";

const WS_CLOSE_NORMAL = 1000;
const EVENT_REMOVE = "REMOVE";

const Home = (props) => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [socket, setSocket] = useState(null);
  const [socketStoreId, setSocketStoreId] = useState(null);
  const { storeId, selectedStoreConfiguration: { isServiceHistoryEnabled } } = useStore();
  const {  
    fetchWorkOrders,
    fetchNewOrders,
    cleanWorkOrders,
    selectedWO, 
    setRemovedWO,
  } = useWorkOrders();
  
  const { setIsLoading, setIsFirstWOLoad } = useLoading();
  const { fetchLicensePlatesToExclude } = useLicensePlates()
  
  useEffect(() => {
    setIsFirstWOLoad(true);
    setSelectedTab(0);
  }, [storeId])

  //* Handle tab changes
  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  //* Fetch work orders
  const getWorkOrders = useCallback(async (useLoading) => {
    if(storeId != 1){
        await fetchWorkOrders(storeId, useLoading)
    }
    else{
      setIsFirstWOLoad(false);
      setIsLoading(false)  
    } 
  },[storeId])


  //* Fetch license plates
  const fetchLicensePlates = useCallback(async () => {
    await fetchLicensePlatesToExclude();
  }, []);

  useEffect(async() => {
    const loadLicensePlates = async () => {
      await fetchLicensePlates();
    };
    await loadLicensePlates();
  }, [fetchLicensePlates]);

  //* Establish WebSocket connection
  const connectToWebsocket = useCallback( () => {
    const socketMessageReceived = async (event, storeId) => {
      const data = JSON.parse(event.data);
      if (data.storeId !== `${storeId}`) return;
      
      data.workOrders.forEach(wo => {
        if(wo.evtName === EVENT_REMOVE){
          setRemovedWO(oldOrders=>[...oldOrders, wo.workOrderId])
        }
      });

      await fetchNewOrders(storeId, data.workOrders);
    }
  
    if (!socket || storeId !== socketStoreId){
      if (socket) {
        console.log('Closing WebSocket connection due to store change');
        socket.close(WS_CLOSE_NORMAL, "switching store");
      }
      const newSocket = new Sockette(`${process.env.REACT_APP_WEBSOCKET_URL}?storeId=${storeId}`, {
        timeout: 300000,
        maxAttempts: 3,
        onopen: () => {
          console.log('WebSocket connected for store: ', storeId)
          getWorkOrders(false) //! new WOs may have arrived while disconnected.
        },
        onmessage: async (e) => {
          try {
            await socketMessageReceived(e, storeId);
          } catch (error) {
            console.error('Error processing WebSocket message: ', error);
          }
        },
        onerror: (e) => console.log('WebSocket encountered an error: ', e),
        onreconnect: () => console.log('Reconnecting to WebSocket...'),
        onclose: (e) => {
          console.log(`WebSocket for store ${storeId} closed with code: ${e.code}`);
          if (e.code !== WS_CLOSE_NORMAL) {
            newSocket.reconnect();
          }
        },
        onmaximum: (e) => {
          console.error('WebSocket reached maximum reconnection attempts: ', e);
        },
      });
      
      setSocket(newSocket);
      setSocketStoreId(storeId);
    }
  },[storeId, socketStoreId, getWorkOrders, socket, selectedWO]);

  //* Establish WebSocket connection and fetch work orders
  useEffect(() => {
    connectToWebsocket()
  }, [storeId, connectToWebsocket]);

  //* Clean work orders when storeId changes
  useEffect(() => {
    cleanWorkOrders();
}, [storeId]);

  return (
    <Paper className="service-recommendations-container" elevation={6}>
      <div>
        {isServiceHistoryEnabled ? (
          <div>
            <AppBar position="static" className="service-recommendations-appbar">
              <Tabs
                value={selectedTab}
                onChange={handleChange}
                indicatorColor="secondary"
                textColor="inherit"
                variant="standard"
              >
                <Tab
                  label="Safety Status Check"
                  className="service-recommendations-tab"
                />
                <Tab
                  label="Service History"
                  className="service-recommendations-tab"
                />
              </Tabs>
            </AppBar>
            <TabPanel value={selectedTab} index={0} dir={theme.direction}>
              <SafetyCheck {...props} />
            </TabPanel>
            <TabPanel value={selectedTab} index={1} dir={theme.direction}>
              <ServiceHistory {...props} />
            </TabPanel>
          </div>
        ) : (
          <div>
            <AppBar position="static" className="service-recommendations-appbar">
              <Tabs
                value={selectedTab}
                onChange={handleChange}
                indicatorColor="secondary"
                textColor="inherit"
                variant="standard"
              >
                <Tab label="Safety Status Check"className="service-recommendations-tab"/>
              </Tabs>
            </AppBar>
            <TabPanel value={selectedTab} index={0} dir={theme.direction}>
              <SafetyCheck {...props} />
            </TabPanel>
          </div>
        )}
        <Loading />
      </div>
    </Paper>
  );
};

export default Home;
